import {
  Button,
  Drawer,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box } from "@mui/system";
import { bool, func, object } from "prop-types";
import { useSnackbar } from "notistack";
import { useAddFeature, useUpdateFeature } from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import { KeyTextField } from "@/components";

type AddFeatureProps = {
  open?: boolean;
  handleClose: (open: boolean) => void;
  row: {
    Id?: string | number | null;
    key?: string | null;
    name?: string | null;
  };
};

export const AddFeature = (props: AddFeatureProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const propsDefault = {
    open: false,
  };

  const [inputFeature, setInputFeature] = useState("");
  const [inputKey, setInputKey] = useState("");

  // Use props.open directly instead of maintaining state
  const open = props.open ?? propsDefault.open;

  useEffect(() => {
    if (props.row.key) {
      // Use setTimeout to defer state updates and avoid cascading renders
      setTimeout(() => {
        setInputFeature(props.row.name);
        setInputKey(props.row.key);
      }, 0);
    } else {
      setTimeout(() => {
        setInputFeature("");
        setInputKey("");
      }, 0);
    }
  }, [props.row]);

  const handleClose = () => {
    props.handleClose(false);
  };

  const resetForm = useCallback(() => {
    setInputKey("");
    setInputFeature("");
  }, []);

  const { addSubscriptionFeature, loading: updatingAdd } = useAddFeature(
    () => {
      resetForm();
      handleClose();
      enqueueSnackbar(gettext("The new feature has been added"), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const { updateSubscriptionFeature, loading: updatingEdit } = useUpdateFeature(
    () => {
      resetForm();
      handleClose();
      enqueueSnackbar(gettext("The role has been updated"), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const variables: any = {
      key: inputKey,
      name: inputFeature,
    };
    if (props.row.Id) {
      variables.id = props.row.Id;
      updateSubscriptionFeature({
        variables: variables,
        refetchQueries: ["GetSubscriptionFeatures"],
      }).then();
    } else {
      addSubscriptionFeature({
        variables: variables,
        refetchQueries: ["GetSubscriptionFeatures"],
      }).then();
    }
  };

  const handleFeatureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputFeature(event.target.value);
  };

  const handleKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputKey(event.target.value);
  };

  return (
    <Drawer
      anchor={"right"}
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
      slotProps={{
        paper: {
          sx: {
            padding: 4,
            maxWidth: 450,
          },
        },
      }}
    >
      <Box
        sx={{
          width: "auto",
        }}
      >
        <form onSubmit={submitHandler}>
          <Stack
            spacing={4}
            sx={{
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
              {!props.row.key && gettext("Add New Feature")}
              {props.row.key && gettext("Edit Feature")}
            </Typography>

            <Box
              sx={{
                height: "100%",
              }}
            >
              <Grid container spacing={4} sx={{ width: "100%" }}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    type="text"
                    label={gettext("Feature Name")}
                    placeholder={gettext("Feature Name")}
                    size="small"
                    required
                    fullWidth
                    value={inputFeature}
                    onChange={handleFeatureChange}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <KeyTextField
                    type="text"
                    label={gettext("Key")}
                    placeholder={gettext(
                      "Feature key, Alpha numeric and dash only",
                    )}
                    size="small"
                    required
                    fullWidth
                    value={inputKey}
                    onChange={handleKeyChange}
                    disabled={!!props.row.key}
                  />
                </Grid>
              </Grid>
            </Box>

            <Paper elevation={0}>
              <Grid container spacing={4} sx={{ width: "100%" }}>
                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleClose()}
                  >
                    {gettext("Cancel")}
                  </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    loading={updatingAdd || updatingEdit}
                    fullWidth
                    type="submit"
                  >
                    {gettext("Save")}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};

AddFeature.propTypes = {
  open: bool,
  handleClose: func,
  row: object,
};
